import React, { useState, useEffect, useRef, useCallback } from "react";
import type { DuelSetup, MatchState, AvatarLayers } from "../types/leagueDuel";
import { createInitialMatch, matchResult } from "../engine/simulation";
import { makeHomeTeam, makeBotTeam } from "../data/mockTeams";
import { DEFAULT_FORMATION } from "../config/formations";
import { loadPlayerElo, savePlayerElo, START_ELO } from "../engine/elo";
import { getDuelEnergy, calculateRegeneratedEnergy, REGEN_MS } from "../engine/energy";
import type { EnergyState } from "../engine/energy";
import { computeChestState, claimChestRemote } from "../engine/dailyChest";
import type { DailyChestState } from "../engine/dailyChest";
import { baseVitality } from "../../../engine/itemEngine";
import { consumeDuelEntry } from "../engine/consumeDuelEntry";
import { randomBotLayers } from "../data/botStuff";
import type { AvatarLayers as AvatarLayersT } from "../types/leagueDuel";
import { getDuelOpponent } from "../engine/duelMatchmaking";
import type { DuelOpponent, OpponentType } from "../engine/duelMatchmaking";
import { calculateDuelRankDelta, saveDuelResult, reputationToRank } from "../engine/duelRanking";
import { calculateTeamPower, calculateMatchAdvantage } from "../engine/combat";
import type { EquipmentStats } from "../engine/combat";
import { sb } from "../../../lib/supabase";
import { LeagueDuelPrep } from "./LeagueDuelPrep";
import { LeagueDuelMatch } from "./LeagueDuelMatch";
import { LeagueDuelResult } from "./LeagueDuelResult";
import { DuelRotationMatch } from "./DuelRotationMatch";
import {
  DUEL_ROTATION_ENABLED,
  createDuelRotationCombat,
  duelRotationSlots,
} from "../engine/duelRotationCombat";
import type { DuelRotationState } from "../engine/duelRotationCombat";

// ─── Orchestrateur du mode ────────────────────────────────────────────────────
// prep → matchmaking → match → result.
//
// Priorité des sources (haute → basse) :
//   1. localDuelCache   — mis à jour après chaque action (spend, result)
//   2. initialDuelData  — boot-preload depuis App.tsx (valeur au démarrage)
//   3. localStorage     — MMR cached (fallback léger)
//   4. Supabase fetch   — seulement si aucune source supérieure disponible
//
// Le cache module-level (localDuelCache) survit aux remounts de LeagueDuelScreen
// (ex: user quitte le hub, revient sur Duel). Il empêche les boot-preload stales
// d'écraser des valeurs issues d'actions locales récentes.

type Phase = "prep" | "matchmaking" | "match" | "result";

// ── Cache module-level — survit aux unmount/remount dans la même session ──────
interface LocalDuelCache {
  mmr: number | null;
  reputation: number | null;
  energy: EnergyState | null;
  updatedAt: number; // Date.now() de la dernière écriture locale
}
let _localCache: LocalDuelCache | null = null;

function writeLocalCache(patch: Partial<Omit<LocalDuelCache, "updatedAt">>) {
  _localCache = {
    mmr: patch.mmr !== undefined ? patch.mmr : (_localCache?.mmr ?? null),
    reputation: patch.reputation !== undefined ? patch.reputation : (_localCache?.reputation ?? null),
    energy: patch.energy !== undefined ? patch.energy : (_localCache?.energy ?? null),
    updatedAt: Date.now(),
  };
}

const ZERO_STATS: EquipmentStats = { technique: 0, defense: 0, physique: 0, vitesse: 0 };

export interface DuelOutcome {
  reputationBefore: number;
  reputationAfter: number;
  reputationDelta: number;
  rankBefore: string;
  rankAfter: string;
  opponentType: OpponentType;
  xpGain: number;
}

export interface InitialDuelData {
  mmr?: number;
  reputation?: number;
  xp?: number;
  energy?: number;
  energyUpdatedAt?: string;
  chestDate?: string | null;
  chestMatches?: number;
  chestClaimed?: boolean;
}

export function LeagueDuelScreen({
  playerName = "You",
  playerProfileId,
  playerAvatar,
  onExit,
  homeLayers,
  playerStats = ZERO_STATS,
  playerLevel = 1,
  initialDuelData,
  onChestClaimed,
  onGoldEarned,
  onXpEarned,
  onRankUpdate,
}: {
  playerName?: string;
  /** UUID du profil actif — utilisé comme attacker_profile_id dans duel_history. */
  playerProfileId?: string | null;
  playerAvatar?: string;
  onExit?: () => void;
  homeLayers?: AvatarLayers;
  playerStats?: EquipmentStats;
  /** Niveau du joueur — alimente le socle de vitalité (appliqué aussi à l'adversaire). */
  playerLevel?: number;
  /** Données duel pré-chargées au boot de l'app — évite le fetch on-mount. */
  initialDuelData?: InitialDuelData;
  /** Appelé après réclamation réussie du coffre — le parent ouvre le RevealModal. */
  onChestClaimed?: () => void;
  /** Appelé quand un match se termine — le parent crédite l'or gagné. */
  onGoldEarned?: (amount: number) => void;
  /** Appelé quand un match se termine — le parent met à jour duel_xp dans le state global. */
  onXpEarned?: (xpAfter: number) => void;
  /** Appelé quand un match se termine — le parent met à jour l'ELO visible (réputation),
   *  le MMR et le rang dans le state global (sinon Lobby + Classement restent figés). */
  onRankUpdate?: (patch: { reputation: number; mmr: number; rank: string }) => void;
}) {
  const [phase, setPhase] = useState<Phase>("prep");
  const [match, setMatch] = useState<MatchState | null>(null);
  const [finalState, setFinalState] = useState<MatchState | null>(null);
  // ── Rotation 1v1 (DUEL_ROTATION_ENABLED) ──
  const [rotationCombat, setRotationCombat] = useState<DuelRotationState | null>(null);
  /** Loadouts de sorts du joueur (players.equipped_spells). null = pas encore chargé. */
  const [spellLoadouts, setSpellLoadouts] = useState<{ attack?: string[]; defense?: string[] } | null>(null);

  // ── Initialisation avec priorité : localCache > boot-preload > localStorage > null ──
  const [playerMmr, setPlayerMmr] = useState<number | null>(() => {
    if (_localCache?.mmr != null) {
      console.log(`[Duel/MMR] source: local-cache (post-action) — mmr=${_localCache.mmr}`);
      return _localCache.mmr;
    }
    if (initialDuelData?.mmr && initialDuelData.mmr > 0) {
      console.log(`[Duel/MMR] source: boot-preload — mmr=${initialDuelData.mmr}`);
      return initialDuelData.mmr;
    }
    const cached = loadPlayerElo();
    if (cached > 0) {
      console.log(`[Duel/MMR] source: localStorage-cache — mmr=${cached}`);
      return cached;
    }
    console.log("[Duel/MMR] source: none — will wait for Supabase fetch");
    return null;
  });
  const [playerReputation, setPlayerReputation] = useState<number | null>(() => {
    if (_localCache?.reputation != null) {
      console.log(`[Duel/Rep] source: local-cache (post-action) — rep=${_localCache.reputation}`);
      return _localCache.reputation;
    }
    if (initialDuelData?.reputation != null) {
      console.log(`[Duel/Rep] source: boot-preload — rep=${initialDuelData.reputation}`);
      return initialDuelData.reputation;
    }
    console.log("[Duel/Rep] source: none — will wait for Supabase fetch");
    return null;
  });
  const [playerXp, setPlayerXp] = useState<number>(() => initialDuelData?.xp ?? 0);
  const [energy, setEnergy] = useState<EnergyState | null>(() => {
    // Only use local cache if it's fresh enough to be accurate (< 1 regen cycle = 30 min).
    // Beyond that, its frozen energy count is stale — fall through to initialDuelData.
    if (_localCache?.energy != null && Date.now() - _localCache.updatedAt < REGEN_MS) {
      console.log(`[Duel/Energy] source: local-cache (post-action, fresh) — current=${_localCache.energy.energy}/8`);
      return _localCache.energy;
    }
    if (initialDuelData?.energy != null && initialDuelData.energyUpdatedAt) {
      const state = calculateRegeneratedEnergy(initialDuelData.energy, initialDuelData.energyUpdatedAt);
      console.log(
        `[Duel/Energy] source: boot-preload — db_base=${initialDuelData.energy}, regen=${Math.max(0, state.energy - initialDuelData.energy)}, current=${state.energy}/8, updatedAt=${initialDuelData.energyUpdatedAt}`
      );
      return state;
    }
    console.log("[Duel/Energy] source: none — will fetch from Supabase on mount");
    return null;
  });

  // Sync energy when initialDuelData arrives late (race: player data loaded after mount).
  // Skipped if local cache is fresher (post-action write within the last regen cycle).
  useEffect(() => {
    if (initialDuelData?.energy == null || !initialDuelData.energyUpdatedAt) return;
    const cacheIsFresh = _localCache != null && Date.now() - _localCache.updatedAt < REGEN_MS;
    if (cacheIsFresh) return;
    const computed = calculateRegeneratedEnergy(initialDuelData.energy, initialDuelData.energyUpdatedAt);
    console.log("[ENERGY] late initialDuelData sync", {
      dbEnergy: initialDuelData.energy,
      updatedAt: initialDuelData.energyUpdatedAt,
      currentEnergy: computed.energy,
    });
    setEnergy(computed);
  }, [initialDuelData?.energy, initialDuelData?.energyUpdatedAt]); // eslint-disable-line

  const [launchStatus, setLaunchStatus] = useState<"idle" | "spending_energy" | "finding_opponent" | "creating_match">("idle");
  const [opponent, setOpponent] = useState<DuelOpponent | null>(null);
  const [awayLayers, setAwayLayers] = useState<AvatarLayersT | undefined>(undefined);
  const [outcome, setOutcome] = useState<DuelOutcome | null>(null);
  // dbChestDate : date brute DB, nécessaire pour détecter le changement de jour dans consumeDuelEntry
  const [dbChestDate, setDbChestDate] = useState<string | null | undefined>(
    () => initialDuelData?.chestDate
  );
  // Flag interne : une action locale a-t-elle modifié le chest dans cette session ?
  // Si true, on ignore les mises à jour tardives de initialDuelData (pas de retour en arrière).
  const chestLocallyUpdatedRef = useRef(false);
  // Guard anti double-clic sur le claim du coffre (même logique que startingRef).
  const claimingChestRef = useRef(false);

  const [chest, setChest] = useState<DailyChestState>(() => {
    console.log("[DailyChest] initial received by LeagueDuelScreen", {
      chestDate: initialDuelData?.chestDate,
      chestMatches: initialDuelData?.chestMatches,
      chestClaimed: initialDuelData?.chestClaimed,
    });
    if (
      initialDuelData?.chestMatches !== undefined ||
      initialDuelData?.chestDate !== undefined
    ) {
      const state = computeChestState(
        initialDuelData.chestDate,
        initialDuelData.chestMatches,
        initialDuelData.chestClaimed
      );
      console.log(`[DailyChest] loaded from boot preload — matches=${state.matches}/${state.goal}, claimed=${state.claimed}`);
      return state;
    }
    console.log("[DailyChest] no preload at mount — starting empty, will sync when data arrives");
    return computeChestState(null, 0, false);
  });

  // ── Sync des données chest tardives (Supabase arrive après le 1er mount) ──
  // La prop initialDuelData peut changer après que LeagueDuelScreen est déjà monté
  // si l'user navigue vers Duel avant la fin du boot fetch.
  // On accepte la mise à jour seulement si aucune action locale n'a eu lieu ET
  // si les données entrantes ont plus de matches (jamais de retour en arrière).
  useEffect(() => {
    if (chestLocallyUpdatedRef.current) {
      console.log("[DailyChest] skipped stale preload — local action is more recent");
      return;
    }
    if (initialDuelData?.chestMatches == null && initialDuelData?.chestDate == null) return;
    const incoming = computeChestState(
      initialDuelData.chestDate,
      initialDuelData.chestMatches,
      initialDuelData.chestClaimed
    );
    setChest((prev) => {
      if (incoming.matches > prev.matches || (incoming.claimed && !prev.claimed)) {
        console.log(
          `[DailyChest] updated from late boot-preload — ${prev.matches} → ${incoming.matches}, claimed=${incoming.claimed}`
        );
        setDbChestDate(initialDuelData.chestDate ?? null);
        return incoming;
      }
      console.log(`[DailyChest] skipped stale preload — local=${prev.matches} >= incoming=${incoming.matches}`);
      return prev;
    });
  }, [initialDuelData?.chestDate, initialDuelData?.chestMatches, initialDuelData?.chestClaimed]); // eslint-disable-line

  // Guard anti-double-spend : bloque dès le 1er clic, avant tout re-render React.
  // TODO avant release publique : remplacer par RPC atomique spend_duel_energy().
  const startingRef = useRef(false);
  // [LeagueDuelFinish] Compteur temporaire — compter les appels de finish par session.
  const finishCallCountRef = useRef(0);

  // ── Chargement initial Supabase — skippé si données suffisamment fraîches ──
  // Priorité : local-cache récent (< 5min) > boot-preload complet > fetch Supabase
  const CACHE_FRESH_MS = 5 * 60 * 1000;
  const hasRecentLocalCache = _localCache != null && Date.now() - _localCache.updatedAt < CACHE_FRESH_MS;
  const hasPreloaded = initialDuelData?.energy != null && initialDuelData.energyUpdatedAt != null;
  useEffect(() => {
    if (hasRecentLocalCache) {
      console.log(`[Duel/Init] local-cache fresh (${Math.round((Date.now() - (_localCache?.updatedAt ?? 0)) / 1000)}s ago) → skipping Supabase fetch`);
      return;
    }
    if (hasPreloaded) {
      console.log("[Duel/Init] boot-preload detected → skipping Supabase on-mount fetch");
      return;
    }
    if (!playerName || playerName === "You") return;
    console.log("[Duel/Init] no preload → fetching duel data from Supabase...");
    const enc = encodeURIComponent(playerName);
    sb.get(
      `players?name=eq.${enc}&select=duel_mmr,duel_reputation,duel_xp,duel_energy,duel_energy_updated_at&limit=1`
    ).then((rows) => {
      const row = rows[0];
      if (!row) {
        console.warn("[Duel/Init] Supabase fetch returned no row");
        return;
      }
      if (typeof row.duel_mmr === "number" && row.duel_mmr > 0) {
        console.log(`[Duel/MMR] source: supabase-fetch — mmr=${row.duel_mmr}`);
        setPlayerMmr(row.duel_mmr);
        savePlayerElo(row.duel_mmr);
      }
      if (typeof row.duel_reputation === "number") {
        console.log(`[Duel/Rep] source: supabase-fetch — rep=${row.duel_reputation}`);
        setPlayerReputation(row.duel_reputation);
      }
      if (typeof row.duel_xp === "number") {
        setPlayerXp(row.duel_xp);
      }
      if (row.duel_energy != null && row.duel_energy_updated_at) {
        const state = calculateRegeneratedEnergy(row.duel_energy, row.duel_energy_updated_at);
        console.log(
          `[Duel/Energy] source: supabase-fetch — db_base=${row.duel_energy}, current=${state.energy}/${8}, updatedAt=${row.duel_energy_updated_at}`
        );
        setEnergy(state);
      }
    });
  }, [playerName]); // eslint-disable-line

  // ── Loadouts de sorts (rotation 1v1) — fetch dédié : le fetch on-mount
  // principal est sauté quand le boot-preload existe, celui-ci tourne toujours. ──
  useEffect(() => {
    if (!DUEL_ROTATION_ENABLED || !playerName || playerName === "You") return;
    sb.get(`players?name=eq.${encodeURIComponent(playerName)}&select=equipped_spells&limit=1`)
      .then((rows) => {
        const es = rows[0]?.equipped_spells;
        if (es && typeof es === "object") {
          setSpellLoadouts({
            attack: Array.isArray(es.attack) ? es.attack : undefined,
            defense: Array.isArray(es.defense) ? es.defense : undefined,
          });
        } else {
          setSpellLoadouts({});
        }
      })
      .catch((e) => {
        console.warn("[Duel/Spells] equipped_spells fetch failed — defaults will be used", e);
        setSpellLoadouts({});
      });
  }, [playerName]);

  /** Persiste les deux rotations dans players.equipped_spells (optimiste). */
  const saveSpellLoadouts = useCallback(async (attack: string[], defense: string[]) => {
    setSpellLoadouts({ attack, defense });
    const ok = await sb.patch("players", `name=eq.${encodeURIComponent(playerName)}`, {
      equipped_spells: { attack, defense },
    });
    if (!ok) console.warn("[Duel/Spells] ⚠️ equipped_spells patch failed — loadouts not persisted");
    else console.log("[Duel/Spells] loadouts saved", { attack, defense });
  }, [playerName]);

  // ── Refresh silencieux toutes les 30s — ne jamais écraser avec une valeur null/invalide ──
  useEffect(() => {
    if (!playerName || playerName === "You") return;
    const iv = setInterval(async () => {
      // On ne rafraîchit que si on est en "prep" (pas pendant un match en cours).
      if (phase !== "prep") return;
      const fresh = await getDuelEnergy(playerName);
      // Patch uniquement si valeur valide — jamais de overwrite avec 0
      setEnergy((prev) => {
        if (fresh.energy >= 0 && fresh.energy <= 8) return fresh;
        console.warn("[Energy] received invalid energy payload, skipping overwrite");
        return prev;
      });
    }, 30000);
    return () => clearInterval(iv);
  }, [playerName, phase]);

  // ── Lancement d'un duel ───────────────────────────────────────────────────
  //
  // Flow : find opponent → create match data → consumeDuelEntry (1 PATCH atomique)
  //        → lancer le match.
  // L'énergie et le coffre ne sont consommés qu'une fois le match prêt.
  // Si consumeDuelEntry échoue (réseau, énergie vide), le match n'est pas lancé.
  const start = useCallback(async (setup: DuelSetup) => {
    // Guard 1 : useRef bloque avant tout re-render (double-click, StrictMode, etc.)
    if (startingRef.current) {
      console.log("[DuelStart] click ignored, already starting");
      return;
    }
    // Guard 2 : phase doit être "prep"
    if (phase !== "prep") {
      console.log("[DuelStart] click ignored, wrong phase:", phase);
      return;
    }
    // Guard 3 : énergie locale insuffisante (check rapide avant appel réseau)
    if (energy !== null && energy.energy < 1) {
      console.log("[DuelStart] click ignored, no energy");
      return;
    }

    const attemptId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    startingRef.current = true;
    let matchLaunched = false;

    console.log(`[DuelStart][${attemptId}] click`);

    try {
      // ── Phase 1 : Trouver un adversaire AVANT de consommer l'énergie ──
      // Si le matchmaking échoue, rien n'est consommé.
      setLaunchStatus("finding_opponent");
      setPhase("matchmaking");
      console.log(`[Matchmaking][${attemptId}] start`);

      // Une seule échelle : la réputation VISIBLE (ce que les joueurs voient à
      // l'écran). Le matchmaking, le gain ELO et l'affichage pilotent tous ce
      // même nombre — on n'utilise plus le MMR caché grande-échelle (qui restait
      // en cache client à ~1400 et re-gonflait le score au match suivant).
      const opp = await getDuelOpponent(playerName, playerReputation ?? START_ELO);
      // Socle de vitalité (stat de personnage selon le niveau) : le joueur l'a déjà
      // via playerStats ; on l'applique symétriquement à l'adversaire (niveau du
      // joueur comme proxy — adversaires appariés par MMR ≈ même niveau) pour que
      // le Duel reste équilibré.
      opp.stats = { ...opp.stats, vitesse: opp.stats.vitesse + baseVitality(playerLevel) };
      console.log(`[DuelStart][${attemptId}] opponent found: ${opp.name} (${opp.opponentType})`);

      // ── Phase 2 : Préparer le match AVANT de consommer l'énergie ──
      setLaunchStatus("creating_match");

      const playerPower = calculateTeamPower(playerStats);
      const opponentPower = calculateTeamPower(opp.stats);
      const advantage = calculateMatchAdvantage(playerPower, opponentPower);

      console.log(`[Combat] player=${playerPower} vs opponent=${opponentPower}, advantage=${advantage.toFixed(3)}`);

      const home = makeHomeTeam(playerName);
      const away = makeBotTeam(opp.name, opp.avatar);
      const initialMatch = createInitialMatch(home, away, setup.formation, DEFAULT_FORMATION, advantage, playerStats, opp.stats);

      // Rotation 1v1 : combat solo vs solo — rotation d'ATTAQUE du joueur
      // contre rotation de DÉFENSE du ghost (défauts si rien d'équipé).
      const rotation = DUEL_ROTATION_ENABLED
        ? createDuelRotationCombat(
            playerName, playerStats, spellLoadouts?.attack,
            opp.name, opp.stats, opp.defenseSpells,
            duelRotationSlots(playerLevel)
          )
        : null;
      if (rotation) {
        console.log("[Duel/Rotation] combat created", {
          attackRotation: rotation.attacker.rotation,
          defenseRotation: rotation.defender.rotation,
          attackerHp: rotation.attacker.maxHp,
          defenderHp: rotation.defender.maxHp,
        });
      }

      // ── Phase 3 : Consommer énergie + coffre en un seul PATCH atomique ──
      // Si ce PATCH échoue, le match n'est pas lancé et l'énergie reste intacte.
      const consumed = await consumeDuelEntry(playerName, chest, dbChestDate, attemptId);

      // Mise à jour locale immédiate (optimiste, confirmé par le PATCH réussi)
      setEnergy(consumed.energy);
      writeLocalCache({ energy: consumed.energy });
      chestLocallyUpdatedRef.current = true;
      setChest(consumed.chest);
      setDbChestDate(consumed.newDbDate);
      console.log(`[DuelStart][${attemptId}] match created`);

      // ── Phase 4 : Lancer le match ──
      setOpponent(opp);
      // Real players: use their actual equipped layers; bots: generate random appearance
      setAwayLayers(
        opp.opponentType === "real_user" ? (opp.layers ?? {}) : randomBotLayers(opp.mmr)
      );
      setMatch(initialMatch);
      setRotationCombat(rotation);
      matchLaunched = true;
      setPhase("match");

    } catch (err) {
      console.error(`[DuelStart][${attemptId}] failed`, err);
      setPhase("prep");
    } finally {
      if (!matchLaunched) {
        startingRef.current = false;
        setLaunchStatus("idle");
      }
    }
  }, [phase, energy, playerName, playerMmr, playerStats, chest, dbChestDate, spellLoadouts]); // eslint-disable-line

  // Reset du guard et du launchStatus quand on revient en prep (après result ou erreur)
  useEffect(() => {
    if (phase === "prep") {
      startingRef.current = false;
      setLaunchStatus("idle");
    }
  }, [phase]);

  // ── Fin de match ─────────────────────────────────────────────────────────
  const finish = (f: MatchState) => {
    finishCallCountRef.current += 1;
    const callN = finishCallCountRef.current;
    console.log("[LeagueDuelFinish] ── appel #" + callN + " ──", {
      timestamp: new Date().toISOString(),
      phase,
      result: matchResult(f),
      homeScore: f.homeScore,
      awayScore: f.awayScore,
      playerXp,
      playerMmr,
      playerReputation,
      opponentName: opponent?.name ?? null,
      opponentType: opponent?.opponentType ?? null,
      opponentProfileId: opponent?.profile_id ?? null,
    });

    setFinalState(f);

    let savePromise: Promise<void> = Promise.resolve();

    if (opponent) {
      const result = matchResult(f);
      const delta = calculateDuelRankDelta(
        playerReputation ?? START_ELO,
        opponent.mmr,
        result,
        opponent.opponentType,
        playerXp
      );

      // Mise à jour locale sans attendre Supabase (pas de flicker)
      setPlayerMmr(delta.mmrAfter);
      setPlayerReputation(delta.repAfter);
      setPlayerXp(delta.xpAfter);
      onXpEarned?.(delta.xpAfter);
      onRankUpdate?.({ reputation: delta.repAfter, mmr: delta.mmrAfter, rank: delta.newRank });
      savePlayerElo(delta.mmrAfter);
      writeLocalCache({ mmr: delta.mmrAfter, reputation: delta.repAfter });
      console.log(`[DuelMeta] local update applied after ranking result — mmr=${delta.mmrAfter}, rep=${delta.repAfter}`);

      const rankBefore = reputationToRank(delta.repBefore);
      console.log(`[Combat] final result: ${result}`);
      console.log(`[Ranking] reputation delta: ${delta.reputationDelta >= 0 ? "+" : ""}${delta.reputationDelta}`);
      console.log(`[Ranking] hidden MMR delta: ${delta.mmrDelta >= 0 ? "+" : ""}${delta.mmrDelta}`);

      setOutcome({
        reputationBefore: delta.repBefore,
        reputationAfter: delta.repAfter,
        reputationDelta: delta.reputationDelta,
        rankBefore,
        rankAfter: delta.newRank,
        opponentType: opponent.opponentType,
        xpGain: delta.xpGain,
      });

      // Fire-and-forget — ne bloque pas setPhase("result")
      savePromise = saveDuelResult(
        playerName, opponent.name, opponent.opponentType, result, delta,
        f.homeScore, f.awayScore,
        playerProfileId ?? null,
        opponent.profile_id ?? null,
        playerAvatar ?? null,
        opponent.mmr
      ).catch((e) => console.error("[Duel] saveDuelResult failed — MMR/reputation/xp non persistés en DB", e));
    }

    // Créditer l'or immédiatement (50 victoire / 25 autre)
    const goldEarned = matchResult(f) === "win" ? 50 : 25;
    console.log("[DuelGoldReward] finish →", { result: matchResult(f), goldEarned });
    onGoldEarned?.(goldEarned);

    // Changement d'écran IMMÉDIAT — saveDuelResult tourne en background
    console.log("[LeagueDuelFinish] setPhase(result) — call #" + callN);
    setPhase("result");

    savePromise.then(() => {
      console.log("[LeagueDuelFinish] saveDuelResult background save done — call #" + callN);
    });
  };

  // ── Fin de combat rotation 1v1 : on synthétise un MatchState minimal (score
  // 1–0) pour réutiliser tel quel le flux finish() existant (ELO, or, XP,
  // duel_history, écran de résultat).
  const finishRotation = (final: DuelRotationState) => {
    if (!match) return;
    const win = final.winner === "attacker";
    console.log("[Duel/Rotation] finished", { winner: final.winner, rounds: final.round });
    finish({ ...match, homeScore: win ? 1 : 0, awayScore: win ? 0 : 1, finished: true });
  };

  const backToPrep = () => {
    setMatch(null);
    setFinalState(null);
    setOutcome(null);
    setOpponent(null);
    setRotationCombat(null);
    setPhase("prep");
  };

  return (
    <>
      {(phase === "prep" || phase === "matchmaking") && (
        <LeagueDuelPrep
          onStart={start}
          onExit={onExit}
          playerName={playerName}
          playerProfileId={playerProfileId}
          playerAvatar={playerAvatar}
          playerReputation={playerReputation ?? START_ELO}
          playerStats={playerStats}
          playerLevel={playerLevel}
          spellAttack={spellLoadouts?.attack}
          spellDefense={spellLoadouts?.defense}
          onSaveSpells={saveSpellLoadouts}
          energy={energy ?? undefined}
          chest={chest}
          launchStatus={launchStatus}
          onClaimChest={async () => {
            // Anti double-clic : un claim déjà en vol bloque les suivants.
            if (claimingChestRef.current) {
              console.log("[DailyChest] claim ignoré — déjà en cours");
              return;
            }
            claimingChestRef.current = true;
            try {
              const newChest = await claimChestRemote(playerName, chest);
              chestLocallyUpdatedRef.current = true;
              setChest(newChest);
              // onChestClaimed (ouverture du pack) uniquement si le claim DB a réussi :
              // claimChestRemote ne renvoie claimed:true que sur PATCH confirmé.
              if (newChest.claimed) onChestClaimed?.();
            } finally {
              claimingChestRef.current = false;
            }
          }}
        />
      )}
      {phase === "match" && match && (
        DUEL_ROTATION_ENABLED && rotationCombat ? (
          <DuelRotationMatch
            initial={rotationCombat}
            homeLayers={homeLayers}
            awayLayers={awayLayers}
            homeAvatar={playerAvatar}
            awayAvatar={opponent?.avatar}
            onFinish={finishRotation}
          />
        ) : (
        <LeagueDuelMatch
          initial={match}
          homeLayers={homeLayers}
          awayLayers={awayLayers}
          onFinish={finish}
          playerStats={playerStats}
          opponentStats={opponent?.stats}
        />
        )
      )}
      {phase === "result" && finalState && (
        <LeagueDuelResult
          state={finalState}
          outcome={outcome}
          onContinue={backToPrep}
        />
      )}
    </>
  );
}
